import { useTranslation } from "react-i18next";
import { CheckInDetail } from "../../models/checkInDetail";
import { Button } from "../common/button";
import { Link, useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { CheckInMemoForm } from "./checkInMemoForm";
import { ApplyConditionsToString } from "../../models/enum/applyCondition";
import { CheckInDetailNoticeCell } from "./checkInDetailNoticeCell";
import { LocalizedDateTime } from "../common/localizedDateTime";
import { CheckInMemoTable } from "./checkInMemoTable";
import { CheckInStatusTable } from "./checkInStatusTable";
import { NoticeEditorComp } from "../notices/noticeEditorComp";
import { BottomModal } from "../common/BottomModal";
import { useAtomValue, useSetAtom } from "jotai";
import { isBackstageUserAtom, userAtom } from "../../atoms/userAtom";
import { LoginType } from "../../models/enum/login";
import { editingNoticeAtom } from "../../atoms/editingNoticeAtom";

export function CheckInDetailTable({
  artistId,
  checkIn,
}: {
  artistId: number;
  checkIn: CheckInDetail;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const postRef = useRef<HTMLDialogElement>(null);
  const [showEditor, setShowEditor] = useState(false);
  const admin = useAtomValue(userAtom);
  const isBackstageUser = useAtomValue(isBackstageUserAtom);
  const setEditingNotice = useSetAtom(editingNoticeAtom);

  const createNotice = () => {
    if (isBackstageUser) {
      setEditingNotice(null);
      navigate("./noticeForm");
    } else {
      setShowEditor(true);
    }
  };

  const onEditorClosed = () => {
    if (!isBackstageUser) setShowEditor(false);
  };

  return (
    <>
      <table className={"table"}>
        <tbody>
          <tr>
            <th>{t("eventdetail_eventid")}</th>
            <td>{checkIn.eventId}</td>
          </tr>
          <tr>
            <th>{t("eventdetail_date")}</th>
            <td>
              {checkIn.eventPeriod ? (
                <LocalizedDateTime dateTime={checkIn.eventPeriod.startAt} />
              ) : (
                t("eventdetail_checkinperiod_tobe")
              )}
            </td>
          </tr>
          <tr>
            <th>{t("eventdetail_condition")}</th>
            <td>{ApplyConditionsToString(checkIn.applyConditions)}</td>
          </tr>
          <tr>
            <th>{t("eventdetail_age")}</th>
            <td>{t("eventdetail_age_value", { age: checkIn.minAge })}</td>
          </tr>
          <tr>
            <th>{t("eventdetail_checkinperiod")}</th>
            <td>
              {checkIn.ticketAvailablePeriod ? (
                <>
                  {checkIn.ticketAvailablePeriod.startAt.format(
                    t("datetime_format"),
                  )}{" "}
                  -{" "}
                  {checkIn.ticketAvailablePeriod.endAt.format(
                    t("datetime_format"),
                  )}
                </>
              ) : (
                t("eventdetail_checkinperiod_tobe")
              )}
            </td>
          </tr>
          <tr>
            <th>
              {t("eventdetail_checkincount")}
              <div className={"py-2"}>
                <Button
                  className={"btn-primary"}
                  onClick={() =>
                    navigate(
                      `/artists/${artistId}/events/${checkIn.eventId}/rewards/${checkIn.rewardId}/notCheckedIn`,
                    )
                  }
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M21.682 2.14656C22.0725 1.75604 22.0725 1.12287 21.682 0.732347C21.2915 0.341823 20.6583 0.341823 20.2678 0.732347L18.5 2.50011L16.7322 0.732346C16.3417 0.341822 15.7085 0.341822 15.318 0.732346C14.9275 1.12287 14.9275 1.75604 15.318 2.14656L17.0858 3.91433L15.318 5.68209C14.9275 6.07262 14.9275 6.70578 15.318 7.09631C15.7085 7.48683 16.3417 7.48683 16.7322 7.09631L18.5 5.32854L20.2678 7.09631C20.6583 7.48683 21.2915 7.48683 21.682 7.09631C22.0725 6.70578 22.0725 6.07262 21.682 5.68209L19.9142 3.91433L21.682 2.14656ZM11 13.9996C12.6569 13.9996 14 12.6564 14 10.9996C14 9.34273 12.6569 7.99958 11 7.99958C9.34315 7.99958 8 9.34273 8 10.9996C8 12.6564 9.34315 13.9996 11 13.9996ZM16 10.9996C16 12.4807 15.356 13.8113 14.3328 14.7269C15.1893 15.1194 15.9778 15.6637 16.6569 16.3427C17.6759 17.3618 18.3917 18.6275 18.746 19.9996C18.9133 20.6475 19 21.3191 19 21.9996H17H11L5 21.9996H3C3 21.3191 3.08671 20.6475 3.25401 19.9996C3.60831 18.6275 4.32406 17.3618 5.34315 16.3427C6.02216 15.6637 6.81067 15.1194 7.66717 14.7269C6.64396 13.8113 6 12.4807 6 10.9996C6 8.23816 8.23858 5.99958 11 5.99958C13.7614 5.99958 16 8.23816 16 10.9996ZM16.6569 19.9996C16.3613 19.1638 15.8815 18.3958 15.2426 17.7569C14.1174 16.6317 12.5913 15.9996 11 15.9996C9.4087 15.9996 7.88258 16.6317 6.75736 17.7569C6.11855 18.3958 5.63866 19.1638 5.34314 19.9996L11 19.9996H16.6569Z"
                      fill="white"
                    />
                  </svg>
                </Button>
              </div>
              <div>
                <Button
                  className={"btn-primary"}
                  onClick={() =>
                    navigate(
                      `/artists/${artistId}/events/${checkIn.eventId}/rewards/${checkIn.rewardId}/checkedIn`,
                    )
                  }
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M22.6635 3.49528C23.0147 3.06903 22.9538 2.43879 22.5276 2.08761C22.1013 1.73643 21.4711 1.79728 21.1199 2.22353L18.103 5.88539L16.6477 4.2048C16.2862 3.78729 15.6546 3.7419 15.2371 4.10343C14.8196 4.46496 14.7742 5.0965 15.1358 5.51401L16.591 7.1946C17.4015 8.13063 18.8592 8.11276 19.6465 7.15714L22.6635 3.49528ZM11 14.0003L11.0029 14.0003C12.6584 13.9987 14 12.6562 14 11.0003C14 9.34342 12.6569 8.00027 11 8.00027C9.34315 8.00027 8 9.34342 8 11.0003C8 12.6571 9.34315 14.0003 11 14.0003ZM16 11.0003C16 12.4813 15.356 13.812 14.3328 14.7276C15.1893 15.1201 15.9778 15.6644 16.6569 16.3434C17.6759 17.3625 18.3917 18.6282 18.746 20.0003C18.9133 20.6482 19 21.3197 19 22.0003H17H11L5 22.0003H3C3 21.3197 3.08671 20.6482 3.25401 20.0003C3.60831 18.6282 4.32406 17.3625 5.34315 16.3434C6.02216 15.6644 6.81067 15.1201 7.66717 14.7276C6.64396 13.812 6 12.4813 6 11.0003C6 8.23885 8.23858 6.00027 11 6.00027C13.7614 6.00027 16 8.23885 16 11.0003ZM11 16.0003L11.0032 16.0003C12.5934 16.0011 14.1182 16.6332 15.2426 17.7576C15.8815 18.3964 16.3613 19.1645 16.6569 20.0003H11L5.34314 20.0003C5.63866 19.1645 6.11855 18.3964 6.75736 17.7576C7.88181 16.6332 9.40663 16.0011 10.9968 16.0003L11 16.0003Z"
                      fill="white"
                    />
                  </svg>
                </Button>
              </div>
            </th>
            <td className={"flex flex-col"}>
              <CheckInStatusTable artistId={artistId} checkIn={checkIn} />
            </td>
          </tr>
          <tr>
            <th className={"align-top"}>
              <div className={"pb-2"}>{t("eventdetail_notice")}</div>
              <Button
                className={`btn-primary ${admin?.source === LoginType.ONE_DAY_STAFF && "hidden"}`}
                onClick={createNotice}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13 11H11V14H8V16H11V19H13V16H16V14H13V11ZM14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z"
                    fill="white"
                  />
                </svg>
              </Button>
            </th>
            <td>
              <div className={"flex justify-end"}>
                <Button
                  className={"btn-ghost btn-sm"}
                  onClick={() =>
                    navigate(
                      `/artists/${artistId}/events/${checkIn.eventId}/rewards/${checkIn.rewardId}/notices`,
                    )
                  }
                >
                  {t("common_btn_more") + " >"}
                </Button>
              </div>
              <div className={"flex flex-col gap-2"}>
                {checkIn.winnerNotices.length === 0 && (
                  <div>
                    <span>{t("eventdetail_notice_emptystate")}</span>
                  </div>
                )}
                {checkIn.winnerNotices.length > 0 &&
                  checkIn.winnerNotices.map((notice) => {
                    return (
                      <Link
                        to={`/artists/${artistId}/events/${checkIn.eventId}/rewards/${checkIn.rewardId}/notices/${notice.id}`}
                        key={notice.id}
                      >
                        <CheckInDetailNoticeCell notice={notice} />
                      </Link>
                    );
                  })}
              </div>
            </td>
          </tr>
          <tr>
            <th className={"align-top"}>
              <div className={"pb-2"}>
                {t("eventdetail_cloudmemo_subtitle")}
              </div>
              <Button
                className={"btn-primary"}
                onClick={() => postRef.current?.showModal()}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13 11H11V14H8V16H11V19H13V16H16V14H13V11ZM14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z"
                    fill="white"
                  />
                </svg>
              </Button>
            </th>
            <td>{<CheckInMemoTable memoList={checkIn.checkInMemos} />}</td>
          </tr>
        </tbody>
      </table>
      <CheckInMemoForm
        eventId={checkIn.eventId}
        rewardId={checkIn.rewardId}
        checkInId={checkIn.id}
        ref={postRef}
      />

      {!isBackstageUser && (
        <BottomModal show={showEditor}>
          <NoticeEditorComp
            initialNotice={null}
            artistId={artistId}
            eventId={checkIn.eventId}
            rewardId={checkIn.rewardId}
            onSave={onEditorClosed}
            onCancel={onEditorClosed}
          />
        </BottomModal>
      )}
    </>
  );
}
